import { FieldProps, FormikProps, getIn } from 'formik'
import React, { Dispatch, SetStateAction, useCallback, useEffect, useState } from 'react'
import Autosuggest, { InputProps, SuggestionsFetchRequestedParams } from 'react-autosuggest'
import { Error, FormikField as Field, Label } from '../../style'
import { AutosuggestFieldContainer, StyledInput } from './styles'
import { GeolocationAutocompleteAddressDetails, Suggestion } from './types'
import { createToken, getAutocomplete, getAutoSuggestPlaceDetails } from '../../../../googlemaps'

interface Props {
  id: string
  label?: string
  name: string
  disabled?: boolean
  required?: boolean
  placeholder?: string
  onAutoSuggestComplete?: (details: GeolocationAutocompleteAddressDetails, form: FormikProps<any>) => void
  onValueChange?: (value: string, form: FormikProps<any>) => void
  onBlur?: (form: FormikProps<any>) => void
  coordsName: string
  zipcodeName: string
  includeNameInAddress: boolean
}

const getSuggestionValue = (suggestion: Suggestion): string => suggestion.placeId

const renderSuggestion = (suggestion: Suggestion): React.ReactNode => (
  <div>
    <b>{suggestion.main} </b>
    {suggestion.secondary}
  </div>
)

const inputChanged = (
  value: string,
  setValue: Dispatch<SetStateAction<string>>,
  form: FormikProps<any>,
  fieldName: string,
  onValueChange?: (value: string, form: FormikProps<any>) => void
) => {
  setValue(value)

  form.setFieldValue(fieldName, value === '' ? null : value)
  form.setFieldTouched(fieldName, true)

  if (onValueChange) {
    onValueChange(value, form)
  }
}

interface CustomAddressInputProps extends InputProps<Suggestion> {
  inputProps?: any
  disabled?: boolean
  hasCoordinates?: boolean
}

const CustomAddressInput: React.FunctionComponent<CustomAddressInputProps> = ({
  inputProps,
  disabled,
  hasCoordinates,
}) => {
  const { key, ...rest } = inputProps
  return <StyledInput key={key} {...rest} disabled={disabled} hasCoordinates={hasCoordinates} />
}

export const AddressField: React.FunctionComponent<Props & FieldProps> = ({
  id,
  field,
  form,
  label,
  placeholder,
  onAutoSuggestComplete,
  onValueChange,
  onBlur,
  coordsName,
  required,
  includeNameInAddress = true,
  disabled = false,
}) => {
  const [value, setValue] = useState<string>(field.value || '')
  const [suggestions, setSuggestions] = useState<Suggestion[]>([])
  const [sessionToken, setSessionToken] = useState<google.maps.places.AutocompleteSessionToken | undefined>()
  const [dropdownDirection, setDropDowndirection] = useState<string>('')

  useEffect(() => {
    setValue(field.value || '')
  }, [field.value])

  const location = getIn(form.values, coordsName)

  const handleChange = useCallback(
    () => async (_: any, { newValue, method }: { newValue: string; method: string }) => {
      if (method === 'click') {
        const placeDetails = await getAutoSuggestPlaceDetails(newValue, includeNameInAddress)

        if (!placeDetails) {
          inputChanged(newValue, setValue, form, field.name, onValueChange)
          return
        }

        inputChanged(placeDetails.address, setValue, form, field.name, onValueChange)

        if (onAutoSuggestComplete) {
          onAutoSuggestComplete(placeDetails, form)
        }

        setSessionToken(undefined)
      } else {
        inputChanged(newValue, setValue, form, field.name, onValueChange)
      }
    },
    [field, form, onAutoSuggestComplete, onValueChange, includeNameInAddress]
  )

  const onSuggestionsFetchRequested = useCallback(
    () => async ({ value: search }: SuggestionsFetchRequestedParams) => {
      const token = sessionToken || (await createToken())
      if (!sessionToken) {
        setSessionToken(token)
      }

      const result = await getAutocomplete(search, token)
      if (!result) {
        console.error('Failed to fetch google places autocomplete suggestions')
        return
      }
      setSuggestions(result)
    },
    [sessionToken]
  )

  const onSuggestionsClearRequested = useCallback(() => {
    setSuggestions([])
  }, [])

  const handleBlur = useCallback(
    () => () => {
      if (!location?.lat && !location?.long && field.value && onBlur) {
        onBlur(form)
      }
    },
    [location?.lat, location?.long, field.value, onBlur, form]
  )

  const fieldError = getIn(form.errors, field.name)
  const fieldTouched = getIn(form.touched, field.name)
  const hasErrors = Boolean(fieldError) && Boolean(fieldTouched)

  return (
    <AutosuggestFieldContainer
      data-testid={`address-input-${id}`}
      onChange={() => {
        const bounding = document.querySelector('.react-autosuggest__suggestions-container')!.getBoundingClientRect()
        const isOutOfView = bounding.bottom + 120 > (window.innerHeight || document.documentElement.clientHeight)

        if (isOutOfView) {
          setDropDowndirection('top')
        } else {
          setDropDowndirection('bottom')
        }
      }}
      direction={dropdownDirection}
    >
      {label && <Label required={required}>{label}</Label>}
      <>
        <Autosuggest
          suggestions={suggestions}
          onSuggestionsFetchRequested={onSuggestionsFetchRequested()}
          onSuggestionsClearRequested={onSuggestionsClearRequested}
          getSuggestionValue={getSuggestionValue}
          renderSuggestion={renderSuggestion}
          inputProps={{
            placeholder,
            value,
            onChange: handleChange(),
            onBlur: handleBlur(),
          }}
          renderInputComponent={(inputProps: InputProps<Suggestion>) => (
            <Field
              id={id}
              component={CustomAddressInput}
              name={field.name}
              disabled={disabled}
              placeholder={placeholder}
              inputProps={inputProps}
              hasCoordinates={location?.lat && location?.long}
            />
          )}
        />
      </>

      {hasErrors && <Error className="error-tooltip">{fieldError}</Error>}
    </AutosuggestFieldContainer>
  )
}
